import { format, parseISO } from 'date-fns';
import { BarChart3 } from 'lucide-react';
import React, { useId, useMemo, useRef, useState } from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';

import type { AnalyticsTimeseriesPoint } from '../types';
import { cn, formatBps, formatDate } from '../utils/formatters';
import { ChartDownloadMenu } from '../../../../shared/ui';
import { useChartExport } from '../../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../../shared/utils/chartFilename';

interface AnalyticsChartProps {
    data: AnalyticsTimeseriesPoint[] | undefined;
}

// --- 1. Institutional Dual-Metric Tooltip ---
const AnalyticsTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
        // Recharts passes the original raw data object inside payload[0].payload
        const dataPoint = payload[0].payload as AnalyticsTimeseriesPoint;
        const spreadVal = dataPoint.spread_10y_2y_bps;
        const volVal = dataPoint.volatility_10y_63d;

        return (
            <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-700 p-3 rounded-xl shadow-xl text-sm min-w-[230px] z-50">
                <div className="font-bold border-b border-slate-200 dark:border-slate-700 pb-2 mb-2 text-slate-800 dark:text-slate-200 uppercase tracking-wider text-xs">
                    {formatDate(label)}
                </div>

                <div className="space-y-2 flex flex-col">
                    {/* Spread Metric */}
                    {spreadVal != null && (
                        <div className="flex justify-between items-center gap-4 text-xs">
                            <span className="flex items-center gap-1.5 font-medium text-slate-600 dark:text-slate-300">
                                <span
                                    className={cn(
                                        'w-2 h-2 rounded-full shadow-sm',
                                        spreadVal < 0 ? 'bg-rose-500' : 'bg-emerald-500',
                                    )}
                                />
                                10Y-2Y Spread
                            </span>
                            <span
                                className={cn(
                                    'font-mono font-bold tracking-tight',
                                    spreadVal < 0
                                        ? 'text-rose-600 dark:text-rose-400'
                                        : 'text-emerald-600 dark:text-emerald-400',
                                )}
                            >
                                {formatBps(spreadVal)}
                            </span>
                        </div>
                    )}

                    {/* Volatility Metric */}
                    {volVal != null && (
                        <div className="flex justify-between items-center gap-4 text-xs">
                            <span className="flex items-center gap-1.5 font-medium text-slate-600 dark:text-slate-300">
                                <span className="w-2 h-2 rounded-full shadow-sm bg-indigo-500" />
                                63D Volatility
                            </span>
                            <span className="font-mono font-bold text-slate-900 dark:text-white tracking-tight">
                                {(volVal * 100).toFixed(1)} bps
                            </span>
                        </div>
                    )}

                    {/* Regime Badge */}
                    <div className="pt-2 mt-1 border-t border-slate-100 dark:border-slate-800">
                        <div className="flex justify-between items-center gap-4 text-xs">
                            <span className="font-medium text-slate-500">Regime:</span>
                            <span className="font-bold text-slate-700 dark:text-slate-300">
                                {dataPoint.market_regime || 'Unknown'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

// --- 2. Main Chart Component ---
export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ data }) => {
    // SVG ID sanitization (React 18+ useId produces colons like :r1:, which breaks SVG url() references)
    const rawId = useId();
    const gradientId = rawId.replace(/:/g, '');

    // UI State for instantaneous timeframe zooming
    const [timeframe, setTimeframe] = useState<'1Y' | '3Y' | '5Y'>('3Y');

    const containerRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(containerRef);

    // 1. Filter the cached 5-year data array down to the requested timeframe locally
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];

        // We expect the data to be sorted oldest to newest from the backend.
        const lastDateStr = data[data.length - 1].date;
        const lastDate = new Date(lastDateStr);

        const yearsToSub = timeframe === '1Y' ? 1 : timeframe === '3Y' ? 3 : 5;
        const cutoffDate = new Date(lastDate);
        cutoffDate.setFullYear(cutoffDate.getFullYear() - yearsToSub);

        return data.filter((d) => new Date(d.date) >= cutoffDate);
    }, [data, timeframe]);

    // 2. Calculate the EXACT percentage offset (0 to 1) where the zero-line sits on the Y-Axis.
    const gradientOffset = useMemo(() => {
        if (!chartData.length) return 0;

        const spreads = chartData
            .map((d) => d.spread_10y_2y_bps)
            .filter((v): v is number => v !== null);
        if (!spreads.length) return 0;

        const max = Math.max(...spreads);
        const min = Math.min(...spreads);

        if (max <= 0) return 1; // Curve strictly inverted (All Red)
        if (min >= 0) return 0; // Curve strictly normal (All Green)

        return max / (max - min);
    }, [chartData]);

    if (!chartData.length) {
        return (
            <div className="flex h-full min-h-[450px] items-center justify-center text-slate-400 text-sm font-medium border border-dashed border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900">
                No timeseries data available.
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-5 md:p-6 flex flex-col h-full min-h-[450px]">
            {/* Header & Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-lg">
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                            Historical Spreads & Volatility
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            Term premium cycles vs Macro volatility.
                        </p>
                    </div>
                </div>

                {/* Segmented Control for Timeframe + Export */}
                <div className="flex items-center gap-2 w-fit">
                    <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700">
                        {(['1Y', '3Y', '5Y'] as const).map((tf) => (
                            <button
                                key={tf}
                                onClick={() => setTimeframe(tf)}
                                className={cn(
                                    'px-3 py-1 text-xs font-bold rounded-md transition-all',
                                    timeframe === tf
                                        ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200',
                                )}
                            >
                                {tf}
                            </button>
                        ))}
                    </div>
                    <ChartDownloadMenu
                        label="Historical Spreads & Volatility chart"
                        onPng={() => downloadPng({ filename: buildChartFilename('spreads-vol', [timeframe]) })}
                        onJpeg={() => downloadJpeg({ filename: buildChartFilename('spreads-vol', [timeframe]) })}
                    />
                </div>
            </div>

            {/* Chart Engine */}
            <div ref={containerRef} className="flex-1 w-full relative min-h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        margin={{ top: 10, right: 8, left: 8, bottom: 0 }}
                    >
                        {/* Split Gradients for both Fill (opacity 0.4) and Stroke (opacity 1) */}
                        <defs>
                            <linearGradient
                                id={`fillGradient-${gradientId}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset={gradientOffset}
                                    stopColor="#10b981"
                                    stopOpacity={0.4}
                                />{' '}
                                {/* Emerald Fill */}
                                <stop
                                    offset={gradientOffset}
                                    stopColor="#f43f5e"
                                    stopOpacity={0.4}
                                />{' '}
                                {/* Rose Fill */}
                            </linearGradient>
                            <linearGradient
                                id={`strokeGradient-${gradientId}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset={gradientOffset}
                                    stopColor="#10b981"
                                    stopOpacity={1}
                                />{' '}
                                {/* Emerald Line */}
                                <stop
                                    offset={gradientOffset}
                                    stopColor="#f43f5e"
                                    stopOpacity={1}
                                />{' '}
                                {/* Rose Line */}
                            </linearGradient>
                        </defs>

                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="currentColor"
                            className="text-slate-200 dark:text-slate-700/50"
                        />

                        {/* X-Axis: Format dates to just "Jan '23" to prevent crowding */}
                        <XAxis
                            dataKey="date"
                            tickFormatter={(dateStr) => {
                                try {
                                    return format(parseISO(dateStr), "MMM ''yy");
                                } catch {
                                    return dateStr;
                                }
                            }}
                            minTickGap={40}
                            tick={{ fontSize: 11, fontWeight: 500 }}
                            tickMargin={10}
                            axisLine={false}
                            tickLine={false}
                            stroke="currentColor"
                            className="text-slate-400 dark:text-slate-500"
                        />

                        {/* Left Axis: Spreads (BPS) */}
                        <YAxis
                            yAxisId="left"
                            width={56}
                            tickFormatter={(value) => `${value} bps`}
                            tick={{ fontSize: 11, fontWeight: 500 }}
                            axisLine={false}
                            tickLine={false}
                            stroke="currentColor"
                            className="text-slate-500 dark:text-slate-400"
                        />

                        {/* Right Axis: Volatility - Hidden to keep UI clean, but scales the Line correctly */}
                        <YAxis yAxisId="right" orientation="right" hide={true} />

                        <Tooltip
                            content={<AnalyticsTooltip />}
                            cursor={{
                                stroke: '#64748b',
                                strokeWidth: 1,
                                strokeDasharray: '4 4',
                            }}
                        />

                        <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                            wrapperStyle={{
                                fontSize: '13px',
                                fontWeight: 500,
                                paddingTop: '20px',
                            }}
                        />

                        {/* Zero Line (The Inversion Threshold) */}
                        <ReferenceLine
                            y={0}
                            yAxisId="left"
                            stroke="#94a3b8"
                            strokeDasharray="3 3"
                        />

                        {/* The Spread Area (Primary Visual) */}
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="spread_10y_2y_bps"
                            name="10Y-2Y Spread"
                            stroke={`url(#strokeGradient-${gradientId})`}
                            strokeWidth={2}
                            fill={`url(#fillGradient-${gradientId})`}
                            isAnimationActive={false}
                            connectNulls
                        />

                        {/* The Volatility Overlay (Secondary Visual) */}
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="volatility_10y_63d"
                            name="63D Volatility"
                            stroke="#6366f1" // Indigo-500
                            strokeWidth={1.5}
                            dot={false}
                            activeDot={{ r: 4, fill: '#6366f1', strokeWidth: 0 }}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default AnalyticsChart;
